/* WF-13 · billing, team & settings — the shell the BI surfaces share.
   The wireframe draws each frame in the merchant chrome (BI-08 with the
   sidebar); this adds only the surface switcher between them.
   FQ-I4 (the record's Paramètres, parcours-marchand-v2 · 12-parametres): « Paramètres » opens on Mon établissement; the
   billing frames are one page, « Offre », with its sections as tabs under its header — five at most, none on the free
   plan (MA-21) — and the actions as buttons on Votre offre. The team's frames (BI-08 · BI-09 · BI-14) and BI-10 are
   Compte et accès's doors: no strip. */
import { Navigate, NavLink, Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import { Button, Card, EmptyState, PageHeader, Skeleton, Tabs } from '@/ui'
import { apiWrites, useCampaigns, useLocations } from '@/lib/api'
import { activeCampaignLimit, allCampaigns, getPlan } from '@/lib/campaign'
import { NAV, NAV_DROP, visibleNav } from '@/lib/nav'
import { currentUser, type Role } from '@/lib/scope'
import { useBilling, type Billing } from './api'

/* ---------- migration v3.3 · WF-126 · who may do what in Paramètres ----------
   Ruled (handover 12/10): billing is the owner's — every other role meets the refusal, never the data (UX18: a page is
   never « simplement masquée »). Not ruled: whether a Manager edits Mon établissement, Marque and Plateformes; the
   default here is « the Manager reads, the owner (and an administrator — « Tout, sauf l’offre et la facturation ») edits »,
   said in the pack's HANDOFF § 3 for the Orchestrator. The demo's role is the world switcher's (lib/scope currentUser). */
/** Billing (Offre and its sections and actions): the owner alone. */
export const isBillingOwner = (role: Role = currentUser().role): boolean => role === 'owner'
/** The settings pages (établissement, marque, plateformes): written by the owner and an administrator, read by the others. */
export const canEditSettings = (role: Role = currentUser().role): boolean => role === 'owner' || role === 'admin'
/** The one line a reader sees on a settings page whose controls are not theirs (⟦confirmable⟧, D-82). */
export const SETTINGS_READ_ONLY_LINE = 'Vous consultez ces réglages : ils se modifient par le propriétaire ou un administrateur.'

/** WF-126 · the refusal state, in place of the page — the two sentences and the one door every role has. */
export function RoleRefusal() {
  const nav = useNavigate()
  return (
    <Card data-testid="role-refus">
      <EmptyState title="Cette action est réservée à un autre rôle."
        why="Demandez au propriétaire de votre établissement."
        action={<Button variant="primary" onClick={() => nav('/caisse')}>Valider un cadeau</Button>} />
    </Card>
  )
}

/** WF-037…041 · the Paramètres strip, one page per tab — the sidebar's own children (lib/nav, never a second list), with its
    rules (one establishment or several, billing owner-only). Links, not `role="tab"`: the Offre page's section tabs keep that role. */
export function ParamTabs() {
  const { pathname } = useLocation()
  const { data: locations } = useLocations()
  const active = (locations ?? []).filter(l => l.status !== 'inactive').length
  const section = visibleNav({ locations: active, role: currentUser().role }).flatMap(g => g.items).find(i => i.path === '/parametres')
  const items = section?.children ?? NAV.flatMap(g => g.items).find(i => i.path === '/parametres')?.children ?? []
  return (
    /* v2 · 390 px: the strip scrolls inside itself — width 0 + min-width 100 % so its nowrap links (516 px of
       min-content) never size the grid track; .ptabs' own overflow-x carries them. */
    <nav aria-label="Paramètres" className="ptabs" data-testid="param-tabs" style={{ width: 0, minWidth: '100%' }}>
      {items.map(c => (
        <NavLink key={c.path} to={c.path} style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
          className={({ isActive }) => 'ptab' + (isActive || (c.also ?? []).some(a => pathname === a || pathname.startsWith(a + '/')) ? ' on' : '')}>{c.label}</NavLink>
      ))}
    </nav>
  )
}

/** Rule 13 · « Quand ces changements prennent-ils effet ? » — the same table on every Paramètres page, with its two doors. */
export const EFFETS: Array<[string, string]> = [
  ['Liens de plateformes', 'Correction immédiate dans les prochaines ouvertures. Les accès des supports concernés seront à revérifier.'],
  ['Marque et règles de participation', 'Les brouillons utilisent ces réglages à leur prochaine publication. Les versions publiées et cadeaux gagnés restent figés.'],
  ['Visuels héritant de la marque', 'Une modification de couleur, logo ou photo rend leur épreuve précédente à revoir. Les commandes préparées restent figées.'],
]
export function EffetTable() {
  const nav = useNavigate()
  return (
    <Card data-testid="effets">
      <p className="t-eyebrow" style={{ marginTop: 0 }}>Quand ces changements prennent-ils effet&nbsp;?</p>
      {/* v2 · 390 px: table.tbl has min-width 480 (ui.css); inside a card that min-content sized the page's grid track
          (scrollWidth 532). contain: inline-size makes the wrap contribute nothing and scroll the table inside itself. */}
      <div className="tablewrap" style={{ contain: 'inline-size' }}>
        <table className="tbl">
          <thead><tr><th>Réglage</th><th>Effet</th></tr></thead>
          <tbody>{EFFETS.map(([k, v]) => <tr key={k}><td>{k}</td><td>{v}</td></tr>)}</tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        <Button size="sm" onClick={() => nav('/campagnes')}>Voir les campagnes à republier</Button>
        <Button size="sm" onClick={() => nav('/supports')}>Voir les supports à vérifier</Button>
      </div>
    </Card>
  )
}

/** The Offre page's sections, in the record's order (the plans, then what the plan costs). */
export const OFFRE_TABS = [
  { frame: 'BI-01', to: 'offre', label: 'Votre offre' },
  { frame: 'BI-03', to: 'utilisation', label: 'Utilisation' },
  { frame: 'BI-07', to: 'factures', label: 'Factures' },
  { frame: 'BI-04', to: 'paiement', label: 'Paiement' },
  { frame: 'BI-06', to: 'profil', label: 'Profil de facturation' },
] as const
/** The Offre page's actions, each a button on Votre offre: they keep « Votre offre » as their section. */
const OFFRE_ACTIONS = ['essai', 'offre/annuel', 'annuler', 'etablissement', 'offre/starter']
/** A paid plan's own pages. No door opens them on the free plan; typed in, they send to Votre offre, as BI-12 and BI-13
    do off Multi (FQ-I4 vet, F2). Utilisation and Essai stay. */
const PAID_ONLY = ['factures', 'paiement', 'profil', 'offre/annuel', 'annuler']
/** Pages that never read the plan: with an API (2b-2) they open without the billing read, which the API does not serve yet. */
const NO_BILLING = ['plateformes', 'integrations', 'organisation']

const surfacePath = (to: string) => `/parametres/${to}`

/** « Paramètres » opens on Mon établissement — « Établissements » from the second one, the sidebar's own rule (NAV_DROP). */
export function ParamIndex() {
  const { data: locations, isPending } = useLocations()
  if (isPending) return null
  const active = (locations ?? []).filter(l => l.status !== 'inactive').length
  return <Navigate replace to={NAV_DROP['/etablissement']({ locations: active }) ? '/etablissements' : '/etablissement'} />
}

export default function ParamShell() {
  const { pathname } = useLocation()
  const path = pathname.replace(/\/$/, '')
  /* the index is a door, not a billing surface: it never waits on the billing data */
  if (path === '/parametres') return <Outlet />
  /* WF-126 · billing is the owner's: any other role typing an Offre path meets the refusal, never the figures */
  if (!isBillingOwner() && BILLING_PATHS.some(to => path === surfacePath(to))) return <div style={{ display: 'grid', gap: 18, maxWidth: 980 }}><ParamTabs /><RoleRefusal /></div>
  if (apiWrites && NO_BILLING.some(to => path === surfacePath(to))) return <div style={{ display: 'grid', gap: 18, maxWidth: 980 }}><ParamTabs /><Outlet /></div>
  if (getPlan() === 'free' && PAID_ONLY.some(to => path === surfacePath(to))) return <Navigate replace to="/parametres/offre" />
  return <BillingShell pathname={pathname} />
}
/** The Offre page, its sections and its actions — what « billing » means for the role guard. */
const BILLING_PATHS: string[] = [...OFFRE_TABS.map(t => t.to), ...OFFRE_ACTIONS]

function BillingShell({ pathname }: { pathname: string }) {
  const { data, isPending, isError, refetch } = useBilling()
  const nav = useNavigate()
  const { data: apiCampaigns } = useCampaigns()
  const { data: places } = useLocations()
  const plan = getPlan()
  const tab = OFFRE_TABS.find(s => pathname === surfacePath(s.to))
  const offre = !!tab || OFFRE_ACTIONS.some(to => pathname === surfacePath(to))
  /* the ceiling counts what Campagnes calls « en ligne » — active, programmée, en pause — as the API's limit does, and says so
     (Orchestrator ruling 09/10: « 2 campagnes sur 3 : en ligne, programmées ou en pause. ») */
  const names = Object.fromEntries((places ?? []).map(l => [l.id, l.name]))
  const live = allCampaigns(apiCampaigns, names).filter(c => c.status === 'active' || c.status === 'scheduled' || c.status === 'paused').length
  const max = activeCampaignLimit(plan)
  const s = live > 1 ? 's' : ''
  return (
    <div style={{ display: 'grid', gap: 18, maxWidth: 980 }}>
      {/* v3.3 · the Paramètres strip above every page of the section (Compte et accès's doors included) */}
      <ParamTabs />
      {/* min-width 0 so the grid track may shrink and .ptabs' own overflow-x
          carries the strip at 390 px, instead of widening the page. */}
      {offre && (
        <div style={{ minWidth: 0 }}>
          <PageHeader title="Offre"
            description={`${live} campagne${s}${max !== null ? ` sur ${max}` : ''}\u00A0: en ligne, programmée${s} ou en pause. Le nombre de personnes dans l’équipe n’est pas facturé.`}
            tabs={plan !== 'free' && (
              <nav aria-label="Offre">
                <Tabs value={tab?.to ?? 'offre'} onChange={to => nav(surfacePath(to))}
                  options={OFFRE_TABS.map(s => [s.to, s.label] as [string, string])} />
              </nav>
            )} />
        </div>
      )}
      {isPending && (
        <div style={{ display: 'grid', gap: 10 }}>
          <Skeleton width="42%" /><Skeleton /><Skeleton width="65%" />
        </div>
      )}
      {isError && (
        <EmptyState title="Impossible de charger vos paramètres."
          why="Votre connexion a peut-être sauté — rien n’a été perdu."
          action={<Button variant="primary" size="sm" onClick={() => refetch()}>Réessayer</Button>} />
      )}
      {data && <Outlet context={data} />}
    </div>
  )
}

export const useBill = () => useOutletContext<Billing>()

/** Frame header: title, and the badge or crumb the wireframe draws beside it.
    Being replaced by @/ui's PageHeader, surface by surface: Équipe (BI-08 · BI-15),
    Paramètres (BI-10) and Équipe — gestion (BI-14) are done (batch 13 ③); the ten
    billing frames still use this. */
export function Head({ title, end }: { title: string; end?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <h2 className="t-section" style={{ flex: 1 }}>{title}</h2>
      {end}
    </div>
  )
}
